import { QueryClient, QueryObserverResult, RefetchOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";

import { request } from "@/utils/request";
import { storage } from "@/utils/storage";
import { camelizeKeys } from "@/utils/object-functions";

type QueryResponse<T> = [
  T | undefined,
  {
    isLoading: boolean,
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<T, Error>>;
  }
];

// Invoked when the API rejects the stored token. Registered by AppProvider,
// which drops the session so the app redirects to sign-in - clearing storage
// alone leaves the in-memory token in place and strands the user on a page
// that can never load.
let onUnauthorized: (() => void) | undefined;

export const setUnauthorizedHandler = (handler?: () => void) => {
  onUnauthorized = handler;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      queryFn: async ({ queryKey }) => {
        const response = await request.get(queryKey[0] as string);

        if (response.status === 401) {
          await storage.remove("authToken");
          onUnauthorized?.();
        }

        return response.json();
      },
    },
  },
});

export const useResource = <T = unknown>(
  path: string,
  options?: Partial<UseQueryOptions<T>>,
): QueryResponse<T> => {
  const { data, isLoading, refetch } = useQuery<T>(
    {
      queryKey: [path],
      ...options,
    },
    queryClient,
  );

  return [camelizeKeys(data) as T, { isLoading, refetch }];
};

export const useCollection = <T = unknown>(
  path: string,
  options?: Partial<UseQueryOptions<Array<T>>>,
): QueryResponse<Array<T>> => {
  const { data, isLoading, refetch } = useQuery<Array<T>>(
    {
      queryKey: [path],
      ...options,
    },
    queryClient,
  );

  return [camelizeKeys(data) as Array<T>, { isLoading, refetch }];
};
